import { memo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { CartContainer } from '@aimsos/ui';

import { formatClockTime } from '../../utils/formatClockTime.js';
import HowToApproachRow from './HowToApproachRow.jsx';

import { Sparkles } from 'lucide-react';

/**
 * The How to approach card beside the Next Best Action (CT-5714): rows from the
 * payload (Reach by, Role, Send, Name — whatever it sends), each opening its
 * source, and the line "How to approach <name> · Concierge · N traits, M
 * insights · Updated <time>" (a zero count is left out; a restricted viewer is
 * told it is based on what they can see).
 *
 * Collapsed, the card is ONE line as in the prototype: the Concierge mark and
 * the first row, cut short. The rest stays mounted, hidden and `inert`.
 */
const HowToApproachCard = memo(function HowToApproachCard({ approach, name, collapsed = false, onOpenSource }) {
  const { t, i18n } = useTranslation('unified-profiles');

  const [first, ...rest] = approach.fields;
  const counts = [
    approach.traitCount ? t('profile.approach.traits', { count: approach.traitCount }) : null,
    approach.insightCount ? t('profile.approach.insights', { count: approach.insightCount }) : null,
  ]
    .filter(Boolean)
    .join(', ');
  const meta = [
    t('profile.approach.title', { name }),
    t('profile.approach.concierge'),
    counts || null,
    t('profile.approach.updated', { time: formatClockTime(approach.updatedAt, i18n?.language) ?? '—' }),
    approach.restricted ? t('profile.approach.restricted') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section
      data-testid="approach-card"
      data-collapsed={collapsed ? 'true' : 'false'}
      aria-label={t('profile.approach.title', { name })}
      className="min-w-0 flex-1"
    >
      <CartContainer size={collapsed ? 'S' : 'M'} variant="neutral" className="h-full">
        <div className={collapsed ? 'flex h-full items-center gap-2' : 'flex h-full flex-col gap-2'}>
          {collapsed ? <Sparkles size={16} aria-hidden className="shrink-0 text-content-muted" /> : null}
          <HowToApproachRow field={first} collapsed={collapsed} onOpenSource={onOpenSource} />
          <div className={collapsed ? 'hidden' : 'flex flex-col gap-2'} hidden={collapsed} inert={collapsed ? '' : undefined}>
            {rest.map((field) => (
              <HowToApproachRow key={field.key} field={field} onOpenSource={onOpenSource} />
            ))}
            <p className="mt-1 px-2 text-xs text-content-muted">{meta}</p>
          </div>
        </div>
      </CartContainer>
    </section>
  );
});

export default HowToApproachCard;
